import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { ArrowUp } from 'lucide-react';
import { AnimatePresence, motion } from 'framer-motion';

const SCROLL_SHOW_THRESHOLD_PX = 200;

/** Above map layers (Leaflet ~7000, legends ~1000, floating log ~9999); below modals (~11000). */
const SCROLL_TO_TOP_Z_INDEX = 10900;

function useScrolledPastThreshold(threshold) {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > threshold);
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [threshold]);

  return scrolled;
}

const ScrollToTopButton = () => {
  const visible = useScrolledPastThreshold(SCROLL_SHOW_THRESHOLD_PX);

  const scrollToTop = useCallback(() => {
    const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({
      top: 0,
      behavior: prefersReducedMotion ? 'auto' : 'smooth',
    });
  }, []);

  return createPortal(
    <AnimatePresence>
      {visible && (
        <motion.button
          type="button"
          aria-label="Scroll to top"
          initial={{ opacity: 0, y: 12, scale: 0.9 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 12, scale: 0.9 }}
          transition={{ duration: 0.2 }}
          onClick={scrollToTop}
          className="vs-scroll-to-top pointer-events-auto fixed left-1/2 flex h-11 w-11 -translate-x-1/2 items-center justify-center rounded-full border border-gray-200/90 bg-white shadow-[0_1px_4px_rgba(15,23,42,0.1),0_2px_8px_rgba(15,23,42,0.06)] active:scale-95"
          style={{
            bottom: 'max(1.5rem, env(safe-area-inset-bottom))',
            zIndex: SCROLL_TO_TOP_Z_INDEX,
          }}
        >
          <ArrowUp className="h-5 w-5 text-gray-600" strokeWidth={1.75} aria-hidden />
        </motion.button>
      )}
    </AnimatePresence>,
    document.body
  );
};

export default ScrollToTopButton;
